import React, {
  useEffect,
} from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import 'objectFitPolyfill';
import { DesignElementKind, DesignElement, DesignCanvasLayer } from 'models/cms/booths';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import getVideoUrlByLocale from 'models/helpers/getVideoUrlByLocale';
import ReactPlayerAutoplay from 'components/ReactPlayerAutoplay';

import BannerPDF from './BannerPDF';
import BannerRichText from './BannerRichText';
import BannerImage from './BannerImage';
import DescriptionBanner from './DescriptionBanner';

const GenericCMSBoothBannerContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`;

export interface GenericCMSBoothBannerContentProps {
  bannerData: DesignElement | DesignCanvasLayer;
  isPreview?: boolean;
  autoPlayVideo?: boolean;
  isDescriptionBanner?: boolean;
  onBannerClick?: () => any;
}

const GenericCMSBoothBannerContent: React.FC<GenericCMSBoothBannerContentProps> = ({
  bannerData: {
    kind,
    name = '',
    image = {},
    video = {},
    contentRichtextHtml = {},
    thumbnailImage = {},
    pdf = {},
    actionUrl = '',
  },
  isPreview = false,
  autoPlayVideo = true,
  isDescriptionBanner = false,
  onBannerClick = () => null,
}: GenericCMSBoothBannerContentProps) => {
  const locale = useSelector(selectLocale);

  useEffect(() => {
    (window as any).objectFitPolyfill();
  }, [image]);

  switch (kind) {
    case DesignElementKind.Image: {
      const imageUrl: string = findAvailableLocale(image, locale)?.originalImageUrl;
      if (!imageUrl) return null;
      return (
        <BannerImage
          name={name}
          imageUrl={imageUrl}
          actionUrl={actionUrl}
        />
      );
    }
    case DesignElementKind.Video: {
      const thumbnailUrl = findAvailableLocale(thumbnailImage, locale)?.originalImageUrl;
      const videoUrl = getVideoUrlByLocale(video, locale);
      if (!videoUrl) return null;
      return <ReactPlayerAutoplay autoPlay={!isPreview && autoPlayVideo} videoUrl={videoUrl} thumbnailUrl={thumbnailUrl} />;
    }
    case DesignElementKind.Richtext: {
      const htmlContent: string = findAvailableLocale(contentRichtextHtml, locale) || '';
      if (isDescriptionBanner) return <DescriptionBanner htmlContent={htmlContent} onViewMoreButtonClick={onBannerClick} />;
      return <BannerRichText htmlContent={htmlContent} />;
    }
    case DesignElementKind.PDF: {
      const pdfUrl = findAvailableLocale(pdf, locale)?.fileUrl;
      const thumbnailUrl = findAvailableLocale(thumbnailImage, locale)?.originalImageUrl;
      if (!(pdfUrl || thumbnailUrl)) return null;
      return <BannerPDF pdfUrl={pdfUrl} thumbnailUrl={thumbnailUrl} />;
    }
    default:
      return null;
  }
};

export interface GenericCMSBoothBannerProps {
  bannerData: DesignElement | DesignCanvasLayer;
  onBannerClick?: () => any;
  isPreview?: boolean;
  autoPlayVideo?: boolean;
  isDescriptionBanner?: boolean;
}

const GenericCMSBoothBanner: React.FC<GenericCMSBoothBannerProps> = React.memo(({
  bannerData,
  onBannerClick = () => null,
  isPreview = false,
  autoPlayVideo = true,
  isDescriptionBanner = false,
}: GenericCMSBoothBannerProps) => (
  <GenericCMSBoothBannerContainer>
    {bannerData && (
      <GenericCMSBoothBannerContent
        bannerData={bannerData}
        isPreview={isPreview}
        onBannerClick={onBannerClick}
        isDescriptionBanner={isDescriptionBanner}
        autoPlayVideo={autoPlayVideo}
      />
    )}
  </GenericCMSBoothBannerContainer>
), (prevProps, nextProps) => (
  prevProps.bannerData?.id === nextProps.bannerData?.id
));

export default GenericCMSBoothBanner;
